import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { systemLanguages } from "./system-languages";
import storage from "@storage";
import { SystemLanguage } from "./types";

const getInstalledLanguages = (defaultLanguage: string) => {
  const { runConfig } = window;
  const languagesStr = runConfig?.languages ?? defaultLanguage;
  return languagesStr.split(",");
};

const defaultLanguage = "en";
const getLang = () => i18n.language || defaultLanguage;
const getLangStruct = () => systemLanguages.find(x => x.id === getLang());
const supportedLanguages = getInstalledLanguages(defaultLanguage);
const supportedLanguagesStruct = (themeLanguage: any) => {
  if (themeLanguage?.length === 0) {
    return systemLanguages.filter(x => supportedLanguages.includes(x.id));
  }

  const langs: SystemLanguage[] =
    themeLanguage?.map(
      (lang: string) => systemLanguages.filter(x => lang === x.id)?.[0]
    ) || [];
  return langs;
};

const loadedNamespaces = new Set<string>();

const fetchBundle = async (lang: string, n: string) => {
  if (i18n.hasResourceBundle(lang, n)) return;
  try {
    const mod = await import(
      /* webpackChunkName: "locale-[request]" */
      `locale/${lang}/${n}.json`
    );
    i18n.addResourceBundle(lang, n, mod.default ?? mod);
  } catch (e: any) {
    if (window.runConfig?.environment === "development") {
      console.warn(
        "[%s] Resource file not found for '%s' language!",
        n,
        lang.toUpperCase()
      );
    }
  }
};

const importLangResources = async (ns: string | string[]) => {
  const namespaces = Array.isArray(ns) ? ns : [ns];
  const activeLang = i18n.language || defaultLanguage;
  namespaces.forEach(n => loadedNamespaces.add(n));
  await Promise.all(namespaces.map(n => fetchBundle(activeLang, n)));
};

const loadAllForLanguage = async (lng: string) => {
  try {
    const mod = await import(
      /* webpackChunkName: "locale-all-[request]" */
      `locale/${lng}/_all.json`
    );
    const bundle = mod.default ?? mod;
    Object.keys(bundle).forEach(ns => {
      if (!i18n.hasResourceBundle(lng, ns)) {
        i18n.addResourceBundle(lng, ns, bundle[ns]);
      }
      loadedNamespaces.add(ns);
    });
  } catch (e: any) {
    if (window.runConfig?.environment === "development") {
      console.warn(
        "[locale] Failed to load combined bundle for '%s' — run `yarn build:locale`",
        lng.toUpperCase()
      );
    }
  }
};

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    lng: storage.get("lang"),
    fallbackLng: defaultLanguage,

    interpolation: {
      escapeValue: false
    }
  });

export {
  i18n as default,
  getLang,
  getLangStruct,
  supportedLanguages,
  supportedLanguagesStruct,
  systemLanguages,
  importLangResources,
  loadAllForLanguage
};
